import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import LocalizedLink from '../components/LocalizedLink';
import LocalizedButton from '../components/Button/LocalizedButton';
import { axiosClient, getImageUrl } from '../services/api';
import { Service } from '@shared/types';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pushToDataLayer, formatServiceItem } from '../utils/dataLayer';
import SEO from '../components/SEO';

interface ExtendedService extends Service {
  slug: string;
  whatIs?: Record<string, string>;
  videoHeading?: Record<string, string>;
  videoUrl?: string;
  videoThumbnailUrl?: string;
  benefitsHeading?: Record<string, string>;
  benefits?: Record<string, string>;
  howItWorksHeading?: Record<string, string>;
  howItWorksBody?: Record<string, string>;
  experienceHeading?: Record<string, string>;
  experienceBody?: Record<string, string>;
  images?: string[];
}

const ServicePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useI18n();

  const [service, setService] = useState<ExtendedService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const serviceName =
    (service?.[`name_${language}` as keyof Service] as string) || service?.name || '';
  // Append localized "in Tallinn" to service name for SEO
  const pageTitle = serviceName ? `${serviceName} ${t.common.inTallinn}` : '';
  useDocumentTitle(pageTitle);

  useEffect(() => {
    if (!slug) return;
    const fetchService = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await axiosClient.get<ExtendedService>(`/api/services/by-slug/${slug}`);
        setService(response.data);
      } catch (err) {
        console.error('Failed to fetch service:', err);
        setError(true);
      }
      setLoading(false);
    };
    fetchService();
  }, [slug]);

  useEffect(() => {
    if (service) {
      pushToDataLayer('view_service', {
        content: { category: 'booking_service' },
        ecommerce: {
          items: [formatServiceItem(service)],
        },
      });
    }
  }, [service]);

  const getText = (field: Record<string, string> | undefined) => {
    if (!field) return '';
    return field[language] || field['en'] || '';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Service not found</h1>
          <LocalizedLink to="/pricing" className="text-blue-600 hover:text-blue-700">
            {t.common.goHome}
          </LocalizedLink>
        </div>
      </div>
    );
  }

  // Get description safely based on slug
  const seoKey = slug as keyof typeof t.seo.services;
  const metaDescription = t.seo.services?.[seoKey] || t.seo.defaultDescription;

  return (
    <div>
      <SEO description={metaDescription} />
      <section
        className="relative overflow-hidden py-28 text-white md:py-56"
        style={{
          backgroundImage: service.videoUrl
            ? 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))'
            : service.videoThumbnailUrl
              ? `url(${getImageUrl(service.videoThumbnailUrl)})`
              : 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {service.videoUrl && (
          <>
            <video
              autoPlay
              muted
              loop
              playsInline
              poster={
                service.videoThumbnailUrl ? getImageUrl(service.videoThumbnailUrl) : undefined
              }
              className="absolute inset-0 z-0 h-full w-full object-cover"
            >
              <source src={service.videoUrl} type="video/mp4" />
            </video>
            <div className="absolute inset-0 z-10 bg-black/50"></div>
          </>
        )}
        {!service.videoUrl && service.videoThumbnailUrl && (
          <div className="absolute inset-0 z-10 bg-black/50"></div>
        )}
        <div className="container relative z-20 mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-4xl font-bold md:text-5xl">{serviceName}</h1>
            <LocalizedButton to={`/book/${service.id}`} name={t.servicePage?.bookNow} size="md" />
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-6xl px-4 py-12">
        {getText(service.whatIs) && (
          <section className="mb-16 text-center">
            {getText(service.videoHeading) && (
              <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
                {getText(service.videoHeading)}
              </h2>
            )}
            <div className="mx-auto max-w-3xl">
              <p
                className="text-lg leading-relaxed text-gray-700"
                dangerouslySetInnerHTML={{ __html: getText(service.whatIs) }}
              />
            </div>
          </section>
        )}
      </div>

      {getText(service.benefits) && (
        <section className="mb-16">
          {service.images && service.images.length > 0 && (
            <div
              className="relative h-96 w-full md:h-[500px]"
              style={{
                backgroundImage: `url(${getImageUrl(service.images[2])})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
              <div className="relative z-10 flex h-full flex-col justify-center">
                <div className="container mx-auto max-w-6xl px-4 md:px-4">
                  <div className="max-w-2xl pl-4 md:pl-0">
                    {getText(service.benefitsHeading) && (
                      <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl">
                        {getText(service.benefitsHeading)}
                      </h2>
                    )}
                    <ul
                      className="space-y-3 text-white [&>li>p]:mb-0 [&>li]:flex [&>li]:items-start [&>li]:space-x-3 [&>li]:leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: getText(service.benefits)
                          .replace(/<li/g, '<li class="flex items-start space-x-3"')
                          .replace(
                            /<li class="flex items-start space-x-3"/g,
                            '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-white rounded-full mt-2"></span><div'
                          )
                          .replace(/<\/li>/g, '</div></li>'),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {(!service.images || service.images.length === 0) && (
            <div className="container mx-auto max-w-6xl px-4">
              <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 p-8 shadow-lg md:p-12">
                {getText(service.benefitsHeading) && (
                  <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
                    {getText(service.benefitsHeading)}
                  </h2>
                )}
                <ul
                  className="space-y-3 text-gray-700 [&>li>p]:mb-0 [&>li]:flex [&>li]:items-start [&>li]:space-x-3"
                  dangerouslySetInnerHTML={{
                    __html: getText(service.benefits)
                      .replace(/<li/g, '<li class="flex items-start space-x-3"')
                      .replace(
                        /<li class="flex items-start space-x-3"/g,
                        '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></span><div'
                      )
                      .replace(/<\/li>/g, '</div></li>'),
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      <div className="container mx-auto max-w-6xl px-4 py-12">
        {getText(service.howItWorksBody) && (
          <section className="mb-16">
            <div className="flex items-start gap-8">
              {/* How it Works Icon/Image */}
              <div className="hidden flex-shrink-0 md:block">
                {service.images && service.images.length > 0 ? (
                  <img
                    src={getImageUrl(service.images[0])}
                    alt={serviceName}
                    className="h-[120px] w-[80px] object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200">
                    <span className="text-sm font-bold text-blue-600">Icon</span>
                  </div>
                )}
              </div>
              {/* How it Works Text */}
              <div className="flex-1 space-y-6">
                {getText(service.howItWorksHeading) && (
                  <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
                    {getText(service.howItWorksHeading)}
                  </h2>
                )}
                <div
                  className="prose prose-lg max-w-none leading-relaxed text-gray-700"
                  dangerouslySetInnerHTML={{ __html: getText(service.howItWorksBody) }}
                />
                {/* Colored line separator - blue, wider, and centered */}
                <div className="mx-auto mt-12 h-0.5 w-3/5 bg-blue-800"></div>
              </div>
            </div>
          </section>
        )}

        {getText(service.experienceBody) && (
          <section className="mb-16">
            {/* Experience Image in its own row */}
            {service.images && service.images.length > 1 && (
              <div className="mb-8 flex justify-center">
                <img
                  src={getImageUrl(service.images[1])}
                  alt={serviceName}
                  className="h-auto w-full object-cover md:w-5/6 md:rounded-2xl"
                />
              </div>
            )}

            {/* Content Block with light background color */}
            <div
              className="rounded-2xl p-8 md:p-12"
              style={{ backgroundColor: 'rgba(229, 240, 254, 0.8)' }}
            >
              {getText(service.experienceHeading) && (
                <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                  {getText(service.experienceHeading)}
                </h2>
              )}
              <ul className="space-y-4 text-gray-700">
                {getText(service.experienceBody)
                  .split('<li>')
                  .filter((item) => item.trim())
                  .map((item, index) => {
                    const cleanItem = item
                      .replace(/<\/li>.*/, '')
                      .replace(/<[^>]*>/g, '')
                      .trim();
                    if (!cleanItem) return null;
                    return (
                      <li key={index} className="flex items-start space-x-3">
                        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600"></span>
                        <span className="leading-relaxed">{cleanItem}</span>
                      </li>
                    );
                  })
                  .filter(Boolean)}
              </ul>
            </div>
          </section>
        )}

        <section className="py-12 text-center">
          <LocalizedButton to={`/book/${service.id}`} name={t.servicePage?.bookNow} />
        </section>
      </div>
    </div>
  );
};

export default ServicePage;
